import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AdminRepository from '../../services/AdminRepository.js';

import SortOrderToggle from '../common/SortOrderToggle.jsx';

import { StatisticsProductDetailPanel } from './ClinicStatisticsLogsPanel.jsx';
import StatisticsAdminHubDetailPanel from './StatisticsAdminHubDetailPanel.jsx';

import {

  formatBytes,

  formatDateKey,

  formatNumber,

  statisticsStatusClass,

  statisticsStatusLabel,

} from '../../utils/statisticsFormat.js';

import { useScreenProbe } from '../../utils/screenTiming.js';

import './AdminStatistics.css';
import './ClinicStatisticsLogsPanel.css';
import './StatisticsAdminHubDetailPanel.css';
import './StatisticsDetailShared.css';



const AdminStatistics = () => {

  const probe = useScreenProbe('statistics');

  const repo = useMemo(() => AdminRepository.getInstance(), []);

  const [summary, setSummary] = useState(null);

  const [productsData, setProductsData] = useState(null);

  const [loading, setLoading] = useState(true);

  const [productsLoading, setProductsLoading] = useState(false);

  const [running, setRunning] = useState(false);

  const [targetDate, setTargetDate] = useState('');

  const [error, setError] = useState('');

  const [notice, setNotice] = useState('');

  const [selectedProductKey, setSelectedProductKey] = useState(null);
  const [selectedAdminHubDomain, setSelectedAdminHubDomain] = useState(null);



  const [search, setSearch] = useState('');

  const [minClinics, setMinClinics] = useState('');

  const [minPatients, setMinPatients] = useState('');

  const [minRecords, setMinRecords] = useState('');

  const [activeOnly, setActiveOnly] = useState(false);

  const [sortKey, setSortKey] = useState('registeredPatients');

  const [sortOrder, setSortOrder] = useState('desc');

  const [topSearch, setTopSearch] = useState('');
  const [topProductKeys, setTopProductKeys] = useState([]);
  const [topDateFrom, setTopDateFrom] = useState('');
  const [topDateTo, setTopDateTo] = useState('');
  const [topSortKey, setTopSortKey] = useState('score');
  const [topSortOrder, setTopSortOrder] = useState('desc');
  const [topRows, setTopRows] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topCounts, setTopCounts] = useState({ total: 0, filtered: 0 });



  const dateKeyToInput = (key) => {
    if (!key || String(key).length !== 8) return '';
    const s = String(key);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  };

  const dateInputToKey = (value) => String(value || '').replace(/-/g, '');



  const loadClinicAppTop = useCallback(async (targetDateKey, overrides = {}) => {
    if (!targetDateKey) return;
    setTopLoading(true);
    try {
      const dateFromKey = overrides.dateFrom !== undefined
        ? dateInputToKey(overrides.dateFrom)
        : dateInputToKey(topDateFrom);
      const dateToKey = overrides.dateTo !== undefined
        ? dateInputToKey(overrides.dateTo)
        : dateInputToKey(topDateTo);
      const params = {
        targetDateKey,
        search: overrides.search !== undefined ? overrides.search : topSearch,
        sort: overrides.sort !== undefined ? overrides.sort : topSortKey,
        order: overrides.order !== undefined ? overrides.order : topSortOrder,
        limit: 100,
      };
      const productKeys = overrides.productKeys !== undefined ? overrides.productKeys : topProductKeys;
      if (productKeys.length > 0) {
        params.productKeys = productKeys.join(',');
      }
      if (dateFromKey && dateToKey) {
        params.dateFrom = dateFromKey;
        params.dateTo = dateToKey;
      }
      const data = await repo.getStatisticsClinicAppTop(params);
      setTopRows(data.rows || []);
      setTopCounts(data.counts || { total: 0, filtered: 0 });
    } catch (err) {
      setError(err.message || '医院×アプリ上位の取得に失敗しました');
    } finally {
      setTopLoading(false);
    }
  }, [repo, topSearch, topProductKeys, topDateFrom, topDateTo, topSortKey, topSortOrder]);



  const loadSummary = useCallback(async (targetDateKey = '') => {

    probe.start('load');

    setLoading(true);

    setError('');

    try {

      const data = await repo.getStatisticsSummary(targetDateKey);

      setSummary(data);

      probe.end('load', 'ok');

      probe.unlock();

      return data;

    } catch (err) {

      probe.end('load', `error: ${err?.message || 'unknown'}`);

      setError(err.message || '統計ログの取得に失敗しました');

      return null;

    } finally {

      setLoading(false);

    }

  }, [repo, probe]);



  const loadProducts = useCallback(async (targetDateKey = '') => {

    probe.start('products');

    setProductsLoading(true);

    try {

      const data = await repo.getStatisticsProducts({

        targetDateKey: targetDateKey || summary?.targetDateKey || '',

        search,

        minClinics,

        minPatients,

        minRecords,

        activeOnly: activeOnly ? '1' : '',

        sort: sortKey,

        order: sortOrder,

      });

      setProductsData(data);

      probe.end('products', 'ok');

    } catch (err) {

      probe.end('products', `error: ${err?.message || 'unknown'}`);

      setError(err.message || '製品別統計ログの取得に失敗しました');

    } finally {

      setProductsLoading(false);

    }

  }, [repo, summary?.targetDateKey, search, minClinics, minPatients, minRecords, activeOnly, sortKey, sortOrder, probe]);



  useEffect(() => {

    loadSummary();

  }, [loadSummary]);



  useEffect(() => {

    if (!summary?.targetDateKey) return undefined;

    const timer = setTimeout(() => {

      loadProducts(summary.targetDateKey);

    }, 250);

    return () => clearTimeout(timer);

  }, [summary?.targetDateKey, loadProducts]);



  useEffect(() => {
    if (!summary?.targetDateKey) return undefined;
    if (!topDateFrom && !topDateTo) {
      setTopDateFrom(dateKeyToInput(summary.targetDateKey));
      setTopDateTo(dateKeyToInput(summary.targetDateKey));
    }
  }, [summary?.targetDateKey]);



  useEffect(() => {
    if (!summary?.targetDateKey) return undefined;
    const timer = setTimeout(() => {
      loadClinicAppTop(summary.targetDateKey);
    }, 300);
    return () => clearTimeout(timer);
  }, [
    summary?.targetDateKey,
    loadClinicAppTop,
    topSearch,
    topProductKeys,
    topDateFrom,
    topDateTo,
    topSortKey,
    topSortOrder,
  ]);



  const handleRunDaily = async () => {

    setRunning(true);

    setError('');

    setNotice('');

    setSelectedProductKey(null);
    setSelectedAdminHubDomain(null);

    try {

      const result = await repo.runDailyStatisticsLog(targetDate);

      setNotice(`日次統計ログを作成しました（${formatDateKey(result.targetDateKey)} / ${statisticsStatusLabel(result.status)}）`);

      const data = await loadSummary(result.targetDateKey);

      if (data?.targetDateKey) {

        await loadProducts(data.targetDateKey);

      }

    } catch (err) {

      setError(err.message || '日次統計ログの作成に失敗しました');

    } finally {

      setRunning(false);

    }

  };



  const adminLog = summary?.adminHubDaily;

  const metrics = adminLog?.metrics || {};

  const indexes = summary?.indexes || {};

  const latestStatus = indexes.productDaily?.latestStatus

    || indexes.clinicAppDaily?.latestStatus

    || indexes.adminHubDaily?.latestStatus;

  const completeness = indexes.clinicAppDaily?.sourceCompleteness;

  const products = productsData?.products || [];

  const clinicAppDailyTop = topRows.length > 0 || topLoading
    ? topRows
    : (summary?.clinicAppDailyTop || []);
  const topProductOptions = products.length > 0
    ? products.map((p) => p.productKey).filter(Boolean)
    : (summary?.productDaily || []).map((p) => p.productKey).filter(Boolean);

  const toggleTopProductKey = (productKey) => {
    setTopProductKeys((prev) => {
      const allKeys = topProductOptions;
      if (allKeys.length === 0) return prev;
      if (prev.length === 0) {
        return allKeys.filter((k) => k !== productKey);
      }
      if (prev.includes(productKey)) {
        return prev.filter((k) => k !== productKey);
      }
      const next = [...prev, productKey];
      if (next.length >= allKeys.length) return [];
      return next;
    });
  };

  const hasTopFilters = Boolean(
    topSearch || topProductKeys.length > 0
    || (topDateFrom && topDateTo && (
      dateInputToKey(topDateFrom) !== summary?.targetDateKey
      || dateInputToKey(topDateTo) !== summary?.targetDateKey
    ))
  );

  const hasProductFilters = Boolean(
    search || minClinics || minPatients || minRecords || activeOnly
  );

  if (selectedAdminHubDomain && summary?.targetDateKey) {
    return (
      <StatisticsAdminHubDetailPanel
        domain={selectedAdminHubDomain}
        targetDateKey={summary.targetDateKey}
        hubMetrics={metrics}
        onBack={() => setSelectedAdminHubDomain(null)}
      />
    );
  }

  return (

    <div className="admin-statistics">

      <div className="section-header stats-header">

        <div>

          <h1>統計</h1>

          <p>統合ログDBに保存された日次統計ログを横断表示します</p>

        </div>

        <button

          type="button"

          className="btn btn-secondary"

          onClick={() => {
            setSelectedProductKey(null);
            setSelectedAdminHubDomain(null);
            loadSummary();
          }}

          disabled={loading || running}

        >

          再読み込み

        </button>

      </div>



      <div className="stats-run-panel">

        <div>

          <h2>日次ログ作成</h2>

          <p>未指定の場合はJST基準の前日分をWorkerで作成します。</p>

        </div>

        <div className="stats-run-controls">

          <input

            type="date"

            value={targetDate}

            onChange={(e) => setTargetDate(e.target.value)}

            aria-label="対象日"

          />

          <button

            type="button"

            className="btn btn-primary"

            onClick={handleRunDaily}

            disabled={running}

          >

            {running ? '実行中...' : '日次ログを作成'}

          </button>

        </div>

      </div>



      {error && <div className="stats-alert danger">{error}</div>}

      {notice && <div className="stats-alert success">{notice}</div>}



      {loading ? (

        <div className="stats-empty">統計ログを読み込んでいます...</div>

      ) : !summary?.targetDateKey ? (

        <div className="stats-empty">統計ログがまだ作成されていません。</div>

      ) : (

        <>

          <div className="stats-status-row">

            <div className="stats-status-card">

              <span>対象日</span>

              <strong>{formatDateKey(summary.targetDateKey)}</strong>

            </div>

            <div className={`stats-status-card ${statisticsStatusClass(latestStatus)}`}>

              <span>最新ステータス</span>

              <strong>{statisticsStatusLabel(latestStatus)}</strong>

            </div>

            <div className="stats-status-card">

              <span>医院×アプリログ</span>

              <strong>{formatNumber(summary.counts?.clinicAppDaily)} 件</strong>

            </div>

            <div className="stats-status-card">

              <span>製品ログ</span>

              <strong>{formatNumber(summary.counts?.productDaily)} 件</strong>

            </div>

          </div>



          {completeness?.hasPartial && (

            <div className="stats-alert warning">

              一部ソースの取得に失敗しています: {(completeness.warnings || []).join(', ')}

            </div>

          )}



          <div className="stats-card-grid">

            <button
              type="button"
              className="stats-metric-card stats-metric-card--clickable"
              onClick={() => {
                setSelectedProductKey(null);
                setSelectedAdminHubDomain('clinics');
              }}
            >
              <span>クリニック</span>
              <strong>{formatNumber(metrics.clinics?.total)}</strong>
              <small>決済登録: {formatNumber(metrics.clinics?.paymentInfoRegistered)} / Stripe連携: {formatNumber(metrics.clinics?.stripeCustomerLinked)}</small>
              <span className="stats-metric-card__hint">詳細を表示 →</span>
            </button>

            <button
              type="button"
              className="stats-metric-card stats-metric-card--clickable"
              onClick={() => {
                setSelectedProductKey(null);
                setSelectedAdminHubDomain('users');
              }}
            >
              <span>ユーザー</span>
              <strong>{formatNumber(metrics.users?.total)}</strong>
              <small>有効: {formatNumber(metrics.users?.active)} / 無効: {formatNumber(metrics.users?.inactive)}</small>
              <span className="stats-metric-card__hint">詳細を表示 →</span>
            </button>

            <button
              type="button"
              className="stats-metric-card stats-metric-card--clickable"
              onClick={() => {
                setSelectedProductKey(null);
                setSelectedAdminHubDomain('contracts');
              }}
            >
              <span>契約</span>
              <strong>{formatNumber(metrics.contracts?.total)}</strong>
              <small>Stripe Subscription: {formatNumber(metrics.contracts?.withStripeSubscription)}</small>
              <span className="stats-metric-card__hint">詳細を表示 →</span>
            </button>

            <div className="stats-metric-card">

              <span>お問い合わせ</span>

              <strong>{formatNumber(metrics.inquiries?.total)}</strong>

              <small>AdminHub業務DB由来</small>

            </div>

          </div>



          {selectedProductKey ? (

            <StatisticsProductDetailPanel

              productKey={selectedProductKey}

              targetDateKey={summary.targetDateKey}

              onBack={() => setSelectedProductKey(null)}

            />

          ) : (

            <section className="stats-section">

              <div className="stats-section-title">

                <div>

                  <h2>製品別集計</h2>

                  <p>製品カードを選択すると詳細ログと医院別内訳を表示します。</p>

                </div>

              </div>



              <div className="list-toolbar stats-detail-toolbar">

                <div className="search-bar list-toolbar__search">

                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">

                    <circle cx="11" cy="11" r="8" />

                    <path d="m21 21-4.35-4.35" />

                  </svg>

                  <input

                    type="text"

                    placeholder="製品名で検索..."

                    value={search}

                    onChange={(e) => setSearch(e.target.value)}

                  />

                </div>

                <input

                  type="number"

                  min="0"

                  className="stats-filter-input"

                  placeholder="医院数下限"

                  value={minClinics}

                  onChange={(e) => setMinClinics(e.target.value)}

                  aria-label="医院数下限"

                />

                <input

                  type="number"

                  min="0"

                  className="stats-filter-input"

                  placeholder="患者数下限"

                  value={minPatients}

                  onChange={(e) => setMinPatients(e.target.value)}

                  aria-label="患者数下限"

                />

                <input

                  type="number"

                  min="0"

                  className="stats-filter-input"

                  placeholder="レコード下限"

                  value={minRecords}

                  onChange={(e) => setMinRecords(e.target.value)}

                  aria-label="レコード下限"

                />

                <label className="stats-filter-checkbox">

                  <input

                    type="checkbox"

                    checked={activeOnly}

                    onChange={(e) => setActiveOnly(e.target.checked)}

                  />

                  アクティブ医院のみ

                </label>

                <div className="list-toolbar__sort">

                  <span className="list-toolbar__sort-label">並び替え</span>

                  <select

                    className="admin-select"

                    value={sortKey}

                    onChange={(e) => setSortKey(e.target.value)}

                    aria-label="並び替え項目"

                  >

                    <option value="productKey">製品名</option>

                    <option value="sourceClinicCount">医院数</option>

                    <option value="activeClinicCount">アクティブ医院</option>

                    <option value="licensedClinicCount">ライセンス保有医院</option>

                    <option value="registeredPatients">登録患者</option>

                    <option value="appDocs">アプリDoc</option>

                    <option value="records">レコード</option>

                    <option value="storageBytes">Storage</option>

                    <option value="updates">更新数</option>

                  </select>

                  <SortOrderToggle

                    order={sortOrder}

                    onToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}

                  />

                </div>

              </div>



              <div className="list-result-bar">

                <div className="list-result-bar__meta">

                  <span className="list-result-count">

                    {formatNumber(productsData?.counts?.filtered)} / {formatNumber(productsData?.counts?.total)} 製品

                  </span>

                  {hasProductFilters && <span className="list-result-hint">フィルタ適用中</span>}

                </div>

              </div>



              {productsLoading ? (

                <div className="stats-empty">製品一覧を読み込んでいます...</div>

              ) : products.length === 0 ? (

                <div className="stats-empty">条件に一致する製品ログがありません。</div>

              ) : (

                <div className="stats-product-grid">

                  {products.map((product) => {

                    const total = product.metrics?.total || {};

                    return (

                      <button

                        key={product.id || product.productKey}

                        type="button"

                        className="stats-product-card"

                        onClick={() => {
                          setSelectedAdminHubDomain(null);
                          setSelectedProductKey(product.productKey);
                        }}

                      >

                        <div className="stats-product-card__header">

                          <strong className="stats-product-card__product-key">{product.productKey}</strong>

                          <span className="stats-product-card__clinic-stats">
                            Docs {formatNumber(product.activeClinicCount)} / {formatNumber(product.sourceClinicCount)} 医院
                          </span>
                          <span className="stats-product-card__clinic-stats">
                            ライセンス {formatNumber(product.licensedClinicCount ?? 0)} 医院
                          </span>

                        </div>

                        <div className="stats-product-card__metrics">

                          <div>

                            <span>登録患者</span>

                            <strong>{formatNumber(total.patient?.registeredPatients)}</strong>

                          </div>

                          <div>

                            <span>レコード</span>

                            <strong>{formatNumber(total.records?.total)}</strong>

                          </div>

                          <div>

                            <span>Storage</span>

                            <strong>{formatBytes(total.media?.storageBytes)}</strong>

                          </div>

                        </div>

                        <div className="stats-product-card__footer">

                          詳細ログを表示 →

                        </div>

                      </button>

                    );

                  })}

                </div>

              )}

            </section>

          )}



          <section className="stats-section">

            <div className="stats-section-title">

              <div>
                <h2>医院×アプリ 上位</h2>
                <p>患者数・レコード数・Storage量を基にした表示。フィルタ・期間・並び替えに対応。</p>
              </div>

            </div>

            <div className="list-toolbar stats-detail-toolbar stats-top-toolbar">

              <div className="search-bar list-toolbar__search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="医院名で検索..."
                  value={topSearch}
                  onChange={(e) => setTopSearch(e.target.value)}
                />
              </div>

              <input
                type="date"
                className="stats-filter-input"
                value={topDateFrom}
                onChange={(e) => setTopDateFrom(e.target.value)}
                title="期間開始"
              />
              <span className="stats-top-date-sep">〜</span>
              <input
                type="date"
                className="stats-filter-input"
                value={topDateTo}
                onChange={(e) => setTopDateTo(e.target.value)}
                title="期間終了"
              />

              <select
                className="stats-filter-input"
                value={topSortKey}
                onChange={(e) => setTopSortKey(e.target.value)}
              >
                <option value="score">総合スコア</option>
                <option value="registeredPatients">登録患者</option>
                <option value="records">レコード</option>
                <option value="storageBytes">Storage</option>
                <option value="updates">更新数</option>
                <option value="newPatients">新規患者</option>
                <option value="clinicName">医院名</option>
                <option value="productKey">製品</option>
              </select>

              <SortOrderToggle
                order={topSortOrder}
                onToggle={() => setTopSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
              />

            </div>

            {topProductOptions.length > 0 && (
              <div className="stats-product-checkboxes">
                <span className="stats-product-checkboxes__label">アプリ（製品）フィルタ</span>
                <div className="stats-product-checkboxes__list">
                  {topProductOptions.map((productKey) => (
                    <label key={productKey} className="stats-product-checkbox">
                      <input
                        type="checkbox"
                        checked={topProductKeys.length === 0 || topProductKeys.includes(productKey)}
                        onChange={() => toggleTopProductKey(productKey)}
                      />
                      <span>{productKey}</span>
                    </label>
                  ))}
                </div>
                {topProductKeys.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary stats-product-checkboxes__clear"
                    onClick={() => setTopProductKeys([])}
                  >
                    製品フィルタをクリア（全選択）
                  </button>
                )}
              </div>
            )}

            <div className="list-result-bar">
              <div className="list-result-bar__meta">
                <span className="list-result-count">
                  {formatNumber(topCounts.filtered)} / {formatNumber(topCounts.total || summary.counts?.clinicAppDaily)} 件
                </span>
                {hasTopFilters && <span className="list-result-hint">フィルタ適用中</span>}
                {topLoading && <span className="list-result-hint">読み込み中...</span>}
              </div>
            </div>

            <div className="stats-table-wrap">

              <table className="stats-table">

                <thead>

                  <tr>

                    <th>医院</th>

                    <th>製品</th>

                    <th>登録患者</th>

                    <th>新規患者</th>

                    <th>レコード</th>

                    <th>Storage</th>

                    <th>更新数</th>

                  </tr>

                </thead>

                <tbody>

                  {clinicAppDailyTop.map((log) => (

                    <tr key={log.id || `${log.clinicId}-${log.productKey}`}>

                      <td>

                        <strong>{log.clinicNameSnapshot || log.clinicId}</strong>

                        <small>{log.clinicId}</small>

                      </td>

                      <td>{log.productKey}</td>

                      <td>{formatNumber(log.metrics?.patient?.registeredPatients)}</td>

                      <td>{formatNumber(log.metrics?.patient?.createdOnTargetDate)}</td>

                      <td>{formatNumber(log.metrics?.records?.total)}</td>

                      <td>{formatBytes(log.metrics?.media?.storageBytes)}</td>

                      <td>{formatNumber(log.metrics?.usage?.firestoreUpdatesOnTargetDate)}</td>

                    </tr>

                  ))}

                  {clinicAppDailyTop.length === 0 && (

                    <tr><td colSpan="7">医院×アプリログがありません。</td></tr>

                  )}

                </tbody>

              </table>

            </div>

          </section>

        </>

      )}

    </div>

  );

};



export default AdminStatistics;

